import React, {	useRef, useState, useCallback, useEffect } from "react";
import AppToolbarButton from "./AppToolbarButton";
import { useTranslation } from "react-i18next";
import './AppHtmlEditor.css';
import { Button } from "react-bootstrap";
import AppColorPalettePopover from "./AppColorPalettePopover";

interface HtmlEditorProps {
	value?: string;
	onChange?: (html: string) => void;
	placeholder?: string;
	minHeight?: number;
	isInvalid: boolean;
}

interface ActiveFormats {
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
	strikeThrough?: boolean;
	insertUnorderedList?: boolean;
	insertOrderedList?: boolean;
	justifyLeft?: boolean;
	justifyCenter?: boolean;
	justifyRight?: boolean;
	fontType?: string;
	fontSize?: string;
}

export default function AppHtmlEditor({
	value = "",
	onChange,
	placeholder = "",
	minHeight = 220,
	isInvalid
}: HtmlEditorProps) {
	const { t } = useTranslation(['common']);

	const editorRef = useRef<HTMLDivElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const savedRangeRef = useRef<Range | null>(null);
	const textColorInputRef = useRef<HTMLInputElement | null>(null);
	const highlightColorInputRef = useRef<HTMLInputElement | null>(null);

	const [isEmpty, setIsEmpty] = useState<boolean>(!value);
	const [activeFormats, setActiveFormats] = useState<ActiveFormats>({
		fontType: "Arial",
		fontSize: '12'
	});
	const [showLinkForm, setShowLinkForm] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [showHtmlSource, setShowHtmlSource] = useState(false);
	const [htmlSource, setHtmlSource] = useState("");
	const [textColor, setTextColor] = useState("#1f1e1c");
	const [highlightColor, setHighlightColor] = useState("#fff59d");
	const [showTextColorPopover, setShowTextColorPopover] = useState(false);
	const [showHighlightPopover, setShowHighlightPopover] = useState(false);
	const textColorArrowRef = useRef<HTMLButtonElement | null>(null);
	const highlightArrowRef = useRef<HTMLButtonElement | null>(null);

	const FONT_FAMILY_OPTIONS = ["Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Trebuchet MS"];
	const FONT_SIZE_OPTIONS = ["8", "9", "10", "11", "12", "14", "16", "18", "20", "22", "24", "26", "32", "48"];

	useEffect(() => {
		if (!editorRef.current) return;

		const incoming = value || "";
		if (editorRef.current.innerHTML !== incoming) {
			editorRef.current.innerHTML = incoming;
			setIsEmpty(!incoming || incoming === "<br>");
		}
	}, [value]);

	const emitChange = useCallback(() => {
		if (editorRef.current && onChange) {
			onChange(editorRef.current.innerHTML);
		}
	}, [onChange]);

	const updateEmptyState = useCallback(() => {
		const text = editorRef.current?.textContent?.trim();
		const html = editorRef.current?.innerHTML?.trim();
		setIsEmpty(!text && (html === "" || html === "<br>"));
	}, []);

	const getElementAtCursor = useCallback((): HTMLElement | null => {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0)
			return null;
		
		let node: Node | null = sel.getRangeAt(0).startContainer;
		if (node.nodeType === Node.TEXT_NODE) 
			node = node.parentElement;
		
		return node as HTMLElement | null;
	}, []);

	const refreshActiveFormats = useCallback(() => {
		setActiveFormats((prev) => ({
			...prev,
			bold: document.queryCommandState("bold"),
			italic: document.queryCommandState("italic"),
			underline: document.queryCommandState("underline"),
			strikeThrough: document.queryCommandState("strikeThrough"),
			insertUnorderedList: document.queryCommandState("insertUnorderedList"),
			insertOrderedList: document.queryCommandState("insertOrderedList"),
			justifyLeft: document.queryCommandState("justifyLeft"),
			justifyCenter: document.queryCommandState("justifyCenter"),
			justifyRight: document.queryCommandState("justifyRight"),
		}));

		const el = getElementAtCursor();
		if (!el || !editorRef.current?.contains(el)) return;

		const computed = window.getComputedStyle(el);

		// Fuente: buscamos cuál de nuestras opciones aparece en el font-family computado
		const rawFontFamily = computed.fontFamily.toLowerCase();
		const matchedFont = FONT_FAMILY_OPTIONS.find((f) =>
			rawFontFamily.includes(f.replace(/'/g, "").toLowerCase())
		);

		// Tamaño: tomamos el px computado y buscamos la opción más cercana
		const sizePx = parseInt(computed.fontSize, 10);
		const matchedSize = !isNaN(sizePx) ? findClosestFontSize(sizePx) : undefined;

		setActiveFormats((prev) => ({
			...prev,
			fontType: matchedFont ?? prev.fontType,
			fontSize: matchedSize ?? prev.fontSize,
		}));

		// Color de texto
		const colorHex = rgbToHex(computed.color);
		if (colorHex) setTextColor(colorHex);

		// Color de resaltado (ignoramos transparente para no pisar el último usado)
		const isTransparentBg =
			computed.background === "rgba(0, 0, 0, 0)" || computed.background === "transparent";
		if (!isTransparentBg) {
			const bgHex = rgbToHex(computed.background);
			if (bgHex) setHighlightColor(bgHex);
		}
	}, [getElementAtCursor]);

	const exec = useCallback(
		(command: string, arg: string | null = null) => {
			editorRef.current?.focus();
			document.execCommand(command, false, arg ?? undefined);
			emitChange();
			updateEmptyState();
			refreshActiveFormats();
		},
		[emitChange, updateEmptyState, refreshActiveFormats]
	);

	const handleInput = () => {
		convertPendingFontSizeTags();
		emitChange();
		updateEmptyState();
	};

	const saveSelection = () => {
		const sel = window.getSelection();
		if (sel && sel.rangeCount > 0) {
			savedRangeRef.current = sel.getRangeAt(0);
		}
	};

	const restoreSelection = () => {
		const sel = window.getSelection();
		if (sel && savedRangeRef.current) {
			sel.removeAllRanges();
			sel.addRange(savedRangeRef.current);
		}
	};

	const openLinkForm = () => {
		saveSelection();
		setLinkUrl("");
		setShowLinkForm(true);
	};

	const confirmLink = () => {
		restoreSelection();
		if (linkUrl.trim()) {
			editorRef.current?.focus();
			document.execCommand("createLink", false, linkUrl.trim());
			emitChange();
		}
		setShowLinkForm(false);
	};

	const handleImageButton = () => fileInputRef.current?.click();

	const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			editorRef.current?.focus();
			document.execCommand("insertImage", false, reader.result as string);
			emitChange();
			updateEmptyState();
		};
		reader.readAsDataURL(file);
		e.target.value = "";
	};

	const pendingHtmlRef = useRef<string | null>(null);
	
	const toggleHtmlSource = () => {
		if (!showHtmlSource) {
			setHtmlSource(editorRef.current?.innerHTML || "");
			setShowHtmlSource(true);
		} else {
			pendingHtmlRef.current = htmlSource;
			setShowHtmlSource(false);
		}
	};
	
	useEffect(() => {
		if (!showHtmlSource && pendingHtmlRef.current !== null && editorRef.current) {
			editorRef.current.innerHTML = pendingHtmlRef.current;
			pendingHtmlRef.current = null;
			emitChange();
			updateEmptyState();
		}
	}, [showHtmlSource, emitChange, updateEmptyState]);

	const clearFormatting = () => exec("removeFormat");

	const pendingFontSizeRef = useRef<string>("12");

	const convertPendingFontSizeTags = useCallback(() => {
		if (!editorRef.current) return;
		const tags = editorRef.current.querySelectorAll('font[size="7"]');

		tags.forEach((el) => {
			if (!el.textContent) return;

			const size = pendingFontSizeRef.current;
			const span = document.createElement("span");
			span.style.fontSize = `${size}px`;

			while (el.firstChild) {
				span.appendChild(el.firstChild);
			}
			el.replaceWith(span);

			const range = document.createRange();
			range.selectNodeContents(span);
			range.collapse(false);
			const sel = window.getSelection();
			sel?.removeAllRanges();
			sel?.addRange(range);
		});
	}, []);

	const applyFontSize = (size: string) => {
		if (!editorRef.current) return;
		pendingFontSizeRef.current = size;
		editorRef.current.focus();
		document.execCommand("fontSize", false, "7");

		const sel = window.getSelection();
		const isCollapsed = !sel || sel.rangeCount === 0 || sel.getRangeAt(0).collapsed;

		if (isCollapsed) {
			emitChange();
			return;
		}

		convertPendingFontSizeTags();
		emitChange();
	};

	const applyFontFamily = (fontFamily: string) => exec("fontName", fontFamily);

	const applyTextColor = (color: string) => {
		if (color === "automatic") {
			exec("removeFormat");
			return;
		}
		setTextColor(color);
		exec("foreColor", color);
	};

	const applyHighlight = (color: string) => {
		if (color === "automatic") {
			editorRef.current?.focus();
			document.execCommand("hiliteColor", false, "transparent");
			emitChange();
			return;
		}
		setHighlightColor(color);

		if (!editorRef.current) return;
		
		editorRef.current.focus();
		const ok = document.execCommand("hiliteColor", false, color);
		if (!ok) document.execCommand("backColor", false, color);
		emitChange();
	};

	const rgbToHex = (rgb: string): string | null => {
		const match = rgb.match(/\d+/g);
		if (!match || match.length < 3) return null;
		const [r, g, b] = match.map(Number);
		return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
	}

	const findClosestFontSize = (px: number): string => {
		return FONT_SIZE_OPTIONS.reduce((closest, size) =>
			Math.abs(Number(size) - px) < Math.abs(Number(closest) - px) ? size : closest
		);
	}

	const Divider = () => <div className="editor-divider" />;

	return (
		<div className={`editor-wrapper ${isInvalid ? "is-invalid" : ""}`}>
			<div className="editor-toolbar">
				<select
					onChange={(e) => {
						const selectedValue = e.target.value;
						if (selectedValue)
							applyFontFamily(selectedValue);
						
						setActiveFormats((prev) => ({ ...prev, fontType: selectedValue }));
					}}
					className="editor-select"
					style={{ width: 140 }}
					title={t("common:htmlEditor.letterType")}
					value={activeFormats.fontType}
				>
					{FONT_FAMILY_OPTIONS.map((fontFamily) => (
						<option key={fontFamily} value={fontFamily}>
						{fontFamily}
						</option>
					))}
				</select>

				<select
					value={activeFormats.fontSize}
					onChange={(e) => {
						const selectedValue = e.target.value;
						if (selectedValue)
							applyFontSize(selectedValue);
						
						setActiveFormats((prev) => ({ ...prev, fontSize: selectedValue }));
					}}
					className="editor-select"
					style={{ width: 50 }}
					title={t("common:htmlEditor.fontSize")}
				>				
					{FONT_SIZE_OPTIONS.map((fontSize) => (
						<option key={fontSize} value={fontSize}>
							{fontSize}
						</option>
					))}
				</select>

				<Divider />
				<div className="editor-color-control" style={{ position: "relative" }} title="Color de texto">
					<button
						type="button"
						className="editor-color-apply"
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => applyTextColor(textColor)}
					>
						<span className="editor-color-icon">A</span>
						<span className="editor-color-bar" style={{ background: textColor }} />
					</button>
					<button
						ref={textColorArrowRef}
						type="button"
						className="editor-color-arrow"
						onMouseDown={(e) => {
							e.preventDefault();
							saveSelection();
							setShowTextColorPopover((v) => !v);
						}}
					>
						▾
					</button>
					{showTextColorPopover && (
						<AppColorPalettePopover
							anchorRef={textColorArrowRef}
							onClose={() => setShowTextColorPopover(false)}
							onSelect={(color) => {
								restoreSelection();
								applyTextColor(color);
							}}
							onMoreColors={() => {
								restoreSelection();
								textColorInputRef.current?.click();
							}}
						/>
					)}
					<input
						ref={textColorInputRef}
						type="color"
						value={textColor}
						onChange={(e) => {
							restoreSelection();
							applyTextColor(e.target.value);
						}}
						className="editor-color-input-hidden"
						tabIndex={-1}
					/>
				</div>

				<div className="editor-color-control" style={{ position: "relative" }} title="Color de resaltado">
					<button
						type="button"
						className="editor-color-apply"
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => applyHighlight(highlightColor)}
					>
						<span className="editor-color-icon"><i className="bi bi-highlighter"></i></span>
						<span className="editor-color-bar" style={{ background: highlightColor }} />
					</button>
					<button
						ref={highlightArrowRef}
						type="button"
						className="editor-color-arrow"
						onMouseDown={(e) => {
							e.preventDefault();
							saveSelection();
							setShowHighlightPopover((v) => !v);
						}}
					>
						▾
					</button>
					{showHighlightPopover && (
						<AppColorPalettePopover
							anchorRef={highlightArrowRef}
							automaticLabel={t("common:htmlEditor.unfilled")}
							onClose={() => setShowHighlightPopover(false)}
							onSelect={(color) => {
								restoreSelection();
								applyHighlight(color);
							}}
							onMoreColors={() => {
								restoreSelection();
								highlightColorInputRef.current?.click();
							}}
						/>
					)}
					<input
						ref={highlightColorInputRef}
						type="color"
						value={highlightColor}
						onChange={(e) => {
							restoreSelection();
							applyHighlight(e.target.value);
						}}
						className="editor-color-input-hidden"
						tabIndex={-1}
					/>
				</div>
				<Divider />

				<AppToolbarButton title="Negrita" active={activeFormats.bold} onMouseDown={() => exec("bold")}><b>N</b></AppToolbarButton>
				<AppToolbarButton title="Cursiva" active={activeFormats.italic} onMouseDown={() => exec("italic")}><i>K</i></AppToolbarButton>
				<AppToolbarButton title="Subrayado" active={activeFormats.underline} onMouseDown={() => exec("underline")}><u>S</u></AppToolbarButton>
				<AppToolbarButton title="Tachado" active={activeFormats.strikeThrough} onMouseDown={() => exec("strikeThrough")}><s>T</s></AppToolbarButton>

				<Divider />

				<AppToolbarButton
					title="Lista con viñetas"
					active={activeFormats.insertUnorderedList}
					onMouseDown={() => exec("insertUnorderedList")}
				>
					<i className="bi bi-list-task" style={{fontSize: 16}}></i>
				</AppToolbarButton>
				<AppToolbarButton
					title="Lista numerada"
					active={activeFormats.insertOrderedList}
					onMouseDown={() => exec("insertOrderedList")}
				>
					<i className="bi bi-list-ol" style={{fontSize: 16}}></i>
				</AppToolbarButton>

				<Divider />

				<AppToolbarButton title="Alinear izquierda" active={activeFormats.justifyLeft} onMouseDown={() => exec("justifyLeft")}><i className="bi bi-text-left" style={{fontSize: 16}}></i></AppToolbarButton>
				<AppToolbarButton title="Centrar" active={activeFormats.justifyCenter} onMouseDown={() => exec("justifyCenter")}><i className="bi bi-text-center" style={{fontSize: 16}}></i></AppToolbarButton>
				<AppToolbarButton title="Alinear derecha" active={activeFormats.justifyRight} onMouseDown={() => exec("justifyRight")}><i className="bi bi-text-right" style={{fontSize: 16}}></i></AppToolbarButton>

				<Divider />

				<AppToolbarButton title="Insertar enlace" onMouseDown={openLinkForm}><i className="bi bi-link-45deg" style={{fontSize: 18}}></i></AppToolbarButton>
				<AppToolbarButton title="Insertar imagen" onMouseDown={handleImageButton}><i className="bi bi-image"></i></AppToolbarButton>

				<Divider />

				<AppToolbarButton title="Deshacer" onMouseDown={() => exec("undo")}><i className="bi bi-arrow-counterclockwise"></i></AppToolbarButton>
				<AppToolbarButton title="Rehacer" onMouseDown={() => exec("redo")}><i className="bi bi-arrow-clockwise"></i></AppToolbarButton>
				<AppToolbarButton title="Limpiar formato" onMouseDown={clearFormatting}>⌫</AppToolbarButton>

				<Divider />

				<AppToolbarButton title="Ver HTML" active={showHtmlSource} onMouseDown={toggleHtmlSource}>{"</>"}</AppToolbarButton>
			</div>

			{showLinkForm && (
				<div className="editor-link-form">
					<input
						type="text"
						autoFocus
						placeholder="https://example.com"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && confirmLink()}
						className="form-control"
					/>
					<Button
						variant="secondary"
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => setShowLinkForm(false)}
					>
						{t("common:htmlEditor.cancel")}
					</Button>
					<Button 
						variant="primary"
						onMouseDown={(e) => e.preventDefault()}
						onClick={confirmLink}
					>
						{t("common:htmlEditor.add")}
					</Button>
				</div>
			)}

			<input ref={fileInputRef} className="d-none" type="file" accept="image/*" onChange={handleImageFile} />

			<div style={{ position: "relative" }}>
				{isEmpty && !showHtmlSource && <div className="editor-placeholder">{placeholder ?? t("common:htmlEditor.writeHere")}</div>}

				{showHtmlSource ? (
					<textarea
						value={htmlSource}
						onChange={(e) => setHtmlSource(e.target.value)}
						className="editor-container"
						style={{ minHeight, fontFamily: "monospace", fontSize: 13 }}
						spellCheck={false}
					/>
				) : (
					<div
						ref={editorRef}
						contentEditable
						suppressContentEditableWarning
						onInput={handleInput}
						onKeyUp={refreshActiveFormats}
						onMouseUp={refreshActiveFormats}
						onFocus={refreshActiveFormats}
						className="editor-container"
						style={{ minHeight, fontFamily: "Arial", fontSize: 12 }}
					/>
				)}
			</div>
		</div>
	);
};
